import React, { useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../context/permissionContext';
import useInactivityTimer from '../../hooks/useInactivityTimer';
import InactivityModal from '../inactivity-modal';
import url from '../../config.js';
import { fullLogout, startLogin, refreshSession, tokenMsRemaining } from '../../auth/oidc.js';
import './protectedroute.scss';

function SessionGuard({ children }) {
  const { logout } = usePermissions();
  const navigate = useNavigate();

  function handleLogout() {
    // Conversation state (smarthub/bf/migration) is intentionally NOT cleared here.
    // This logout is triggered by inactivity — the user didn't choose to start over.
    // State is keyed by userId so it restores automatically on next login for the same account.
    localStorage.removeItem('langraph_session_id');
    logout();
    // End the Keycloak SSO session too for Keycloak logins (else silent re-login).
    fullLogout();
  }

  async function handleRelogin(password) {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const res = await fetch(`${url.domain}/api/auth/signin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: storedUser.username, password }),
    });
    if (!res.ok) throw new Error('Invalid credentials');
    const data = await res.json();
    localStorage.setItem('token', data.accessToken);
    localStorage.setItem('user', JSON.stringify({
      id: data.id,
      username: data.username,
      email: data.email,
      roles: data.roles,
      permission: data.permission,
      tenantId: data.tenantId,
      pagePermissions: data.pagePermissions || {},
    }));
  }

  // Keycloak / Google users have no app password, so password re-auth can't work for them.
  // Re-authenticate through Keycloak instead (SSO cookie usually still valid → effectively silent).
  let storedUser = {};
  try { storedUser = JSON.parse(localStorage.getItem('user') || '{}'); } catch (_) {}
  const isKeycloak = storedUser.authProvider === 'keycloak' || url.passwordLogin === false;

  const { showWarning, secondsLeft, dismissWarning } = useInactivityTimer({
    timeoutMs: 30 * 60 * 1000,
    countdownMs: 60 * 1000,
    onLogout: handleLogout,
    enabled: true,
  });

  // Idle "Stay Logged In" for a Keycloak user: refresh silently and STAY on the current
  // page. Only if the silent refresh fails (SSO ended / refresh expired) fall back to a
  // full Keycloak redirect. Returns true when it kept the user in place.
  async function handleKeycloakRelogin() {
    const ok = await refreshSession();
    if (ok) { dismissWarning(); return true; }
    startLogin();
    return false;
  }

  // Proactive silent refresh: renew the Intelog JWT ~2 min before it expires so the
  // session rarely dies under the user. Keycloak-only (needs the refresh token); reschedules
  // off each new token. A failed refresh is a no-op here — a 401 or the idle flow handles it.
  const refreshTimer = useRef(null);
  useEffect(() => {
    if (!isKeycloak) return undefined;
    let cancelled = false;
    const schedule = () => {
      const ms = tokenMsRemaining();
      const delay = ms == null ? 10 * 60 * 1000 : Math.max(ms - 2 * 60 * 1000, 5000);
      refreshTimer.current = setTimeout(async () => {
        const ok = await refreshSession();
        if (!cancelled && ok) schedule();
      }, delay);
    };
    schedule();
    return () => { cancelled = true; if (refreshTimer.current) clearTimeout(refreshTimer.current); };
  }, [isKeycloak]);

  return (
    <React.Fragment>
      {children}
      <InactivityModal
        show={showWarning}
        secondsLeft={secondsLeft}
        onRelogin={handleRelogin}
        onStay={dismissWarning}
        onLogout={handleLogout}
        isKeycloak={isKeycloak}
        onKeycloakRelogin={handleKeycloakRelogin}
      />
    </React.Fragment>
  );
}

const ProtectedRoute = ({ children, requiredPermission }) => {
  const { hasPermission } = usePermissions();

  const user = localStorage.getItem('user');
  if (!user || user === 'null') {
    return <Navigate to="/" replace />;
  }

  const permitted = !requiredPermission || (
    Array.isArray(requiredPermission)
      ? requiredPermission.some(p => hasPermission(p))
      : hasPermission(requiredPermission)
  );
  if (!permitted) {
    return (
      <div className="access-denied-container">
        <div className="access-denied-icon">🚫</div>
        <h2 className="access-denied-title">Access Denied</h2>
        <p className="access-denied-description">
          You don't have permission to access this page. Please contact your administrator if you believe this is an error.
        </p>
        <button
          className="access-denied-button"
          onClick={() => window.history.back()}
        >
          ← Go Back
        </button>
      </div>
    );
  }

  return <SessionGuard>{children}</SessionGuard>;
};

export default ProtectedRoute;